<template>
  <div class="page-container animate-fade-in-up">
    <PageHeader icon="el-icon-s-custom" title="管理员列表" subtitle="管理系统管理员账号，分配任务范围" />

    <!-- 工具栏 -->
    <div class="toolbar">
      <el-input
        v-model="search"
        placeholder="搜索姓名、邮箱"
        prefix-icon="el-icon-search"
        clearable
        style="width: 260px;"
      />
      <el-select v-model="filterRole" placeholder="角色" clearable style="width: 140px; margin-left: 12px;">
        <el-option label="超级管理员" value="super_admin" />
        <el-option label="普通管理员" value="admin" />
      </el-select>
      <el-select v-model="filterStatus" placeholder="状态" clearable style="width: 120px; margin-left: 12px;">
        <el-option label="启用" :value="1" />
        <el-option label="禁用" :value="0" />
      </el-select>
      <el-button
        v-if="hasActiveFilter"
        type="text"
        style="margin-left: 12px;"
        @click="clearFilters"
      >
        清除筛选
      </el-button>
      <el-button type="primary" icon="el-icon-plus" class="btn-click" style="margin-left: auto;" @click="openCreateDialog">
        新增管理员
      </el-button>
    </div>

    <!-- 空状态 -->
    <el-empty v-if="filteredList.length === 0" description="暂无匹配的管理员">
      <el-button v-if="hasActiveFilter" type="text" @click="clearFilters">清除筛选</el-button>
    </el-empty>

    <!-- 表格 -->
    <div v-else class="table-wrapper">
      <el-table :data="filteredList" stripe class="data-table" style="width: 100%">
        <el-table-column prop="admin_id" label="ID" width="60" />
        <el-table-column prop="real_name" label="姓名" min-width="100" />
        <el-table-column prop="email" label="邮箱" min-width="180" />
        <el-table-column label="角色" width="110">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.role === 'super_admin'" type="warning" size="small">超级管理员</el-tag>
            <el-tag v-else type="primary" size="small">普通管理员</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="负责任务" min-width="180">
          <template slot-scope="scope">
            <div v-if="scope.row.role === 'super_admin'" class="text-muted">全部任务</div>
            <div v-else-if="scope.row.task_ids && scope.row.task_ids.length">
              <el-tag v-for="tid in scope.row.task_ids" :key="tid" size="mini" type="info" style="margin: 0 4px 4px 0;">{{ taskNameMap[tid] || ('任务' + tid) }}</el-tag>
            </div>
            <span v-else class="text-muted">未分配</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="80" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="small">
              {{ scope.row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="110" align="center" />
        <el-table-column label="操作" min-width="200">
          <template slot-scope="scope">
            <el-button type="text" class="action-edit" @click="openEditDialog(scope.row)">编辑</el-button>
            <el-button v-if="scope.row.role !== 'super_admin' || canDeleteSuperAdmin(scope.row)" type="text" :class="scope.row.status === 1 ? 'action-disable' : 'action-edit'" @click="toggleStatus(scope.row)">
              {{ scope.row.status === 1 ? '禁用' : '启用' }}
            </el-button>
            <el-button v-if="canDelete(scope.row)" type="text" class="action-disable" @click="removeAdmin(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 新增管理员弹窗 -->
    <el-dialog title="新增普通管理员" :visible.sync="createDialogVisible" width="460px" :append-to-body="true">
      <el-form :model="createForm" label-width="90px">
        <el-form-item label="邮箱" required>
          <el-input v-model="createForm.email" placeholder="请输入邮箱" />
        </el-form-item>
        <el-form-item label="姓名" required>
          <el-input v-model="createForm.real_name" placeholder="请输入姓名" />
        </el-form-item>
        <el-form-item label="初始密码" required>
          <el-input v-model="createForm.password" placeholder="请输入初始密码" show-password />
        </el-form-item>
        <el-form-item label="分配任务">
          <el-select v-model="createForm.task_ids" multiple placeholder="选择该管理员负责的任务" style="width: 100%;">
            <el-option
              v-for="task in allTasks"
              :key="task.task_id"
              :label="task.task_name"
              :value="task.task_id"
            />
          </el-select>
          <div class="form-hint">不选则该管理员登录后无法查看任何任务数据</div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveCreate">创建</el-button>
      </span>
    </el-dialog>

    <!-- 编辑管理员弹窗 -->
    <el-dialog title="编辑管理员" :visible.sync="editDialogVisible" width="460px" :append-to-body="true">
      <el-form v-if="editForm" :model="editForm" label-width="90px">
        <el-form-item label="邮箱">
          <el-input v-model="editForm.email" disabled />
        </el-form-item>
        <el-form-item label="姓名" required>
          <el-input v-model="editForm.real_name" placeholder="请输入姓名" />
        </el-form-item>
        <el-form-item label="重置密码">
          <el-checkbox v-model="editForm.resetPassword">重置密码</el-checkbox>
        </el-form-item>
        <el-form-item v-if="editForm.resetPassword" label="新密码" required>
          <el-input v-model="editForm.newPassword" placeholder="请输入新密码" show-password />
        </el-form-item>
        <el-form-item label="分配任务">
          <el-select v-model="editForm.task_ids" multiple placeholder="选择该管理员负责的任务" style="width: 100%;">
            <el-option
              v-for="task in allTasks"
              :key="task.task_id"
              :label="task.task_name"
              :value="task.task_id"
            />
          </el-select>
          <div class="form-hint">不选则该管理员登录后无法查看任何任务数据</div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveEdit">保存</el-button>
      </span>
    </el-dialog>

    <!-- 任务分配弹窗 -->
    <el-dialog title="任务分配" :visible.sync="taskDialogVisible" width="460px" :append-to-body="true">
      <div v-if="currentAdmin">
        <div class="section-title">{{ currentAdmin.real_name }} — 负责的任务</div>
        <el-select v-model="taskForm.task_ids" multiple placeholder="选择该管理员负责的任务" style="width: 100%;">
          <el-option
            v-for="task in allTasks"
            :key="task.task_id"
            :label="task.task_name"
            :value="task.task_id"
          />
        </el-select>
        <div class="form-hint">不选则该管理员登录后无法查看任何任务数据</div>
      </div>
      <span slot="footer">
        <el-button @click="taskDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveTasks">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '../../components/PageHeader.vue'
import { rawGet } from '../../utils/storage'

export default {
  name: 'AdminManage',
  components: { PageHeader },
  data() {
    return {
      search: '',
      filterRole: '',
      filterStatus: '',
      createDialogVisible: false,
      createForm: { email: '', real_name: '', password: '', task_ids: [] },
      editDialogVisible: false,
      editForm: null,
      taskDialogVisible: false,
      currentAdmin: null,
      taskForm: { task_ids: [] }
    }
  },
  computed: {
    admins() {
      return this.$store.state.admins.admins || []
    },
    allTasks() {
      return (this.$store.state.tasks && this.$store.state.tasks.tasksList) || []
    },
    taskNameMap() {
      const map = {}
      this.allTasks.forEach(t => { map[t.task_id] = t.task_name })
      return map
    },
    hasActiveFilter() {
      return this.search || this.filterRole !== '' || this.filterStatus !== ''
    },
    filteredList() {
      let list = [...this.admins]
      if (this.search) {
        const kw = this.search.toLowerCase()
        list = list.filter(a =>
          (a.real_name && a.real_name.toLowerCase().includes(kw)) ||
          (a.email && a.email.toLowerCase().includes(kw))
        )
      }
      if (this.filterRole) {
        list = list.filter(a => a.role === this.filterRole)
      }
      if (this.filterStatus !== '') {
        list = list.filter(a => a.status === this.filterStatus)
      }
      list.sort((a, b) => {
        const ta = a.created_at || ''
        const tb = b.created_at || ''
        return tb.localeCompare(ta)
      })
      return list
    }
  },
  methods: {
    clearFilters() {
      this.search = ''
      this.filterRole = ''
      this.filterStatus = ''
    },
    openCreateDialog() {
      this.createForm = { email: '', real_name: '', password: '', task_ids: [] }
      this.createDialogVisible = true
    },
    async saveCreate() {
      const { email, real_name, password } = this.createForm
      if (!email.trim() || !real_name.trim() || !password.trim()) {
        this.$message.error('请填写完整信息')
        return
      }
      const exists = this.admins.find(a => a.email === email.trim())
      if (exists) {
        this.$message.error('该邮箱已被使用，请更换')
        return
      }
      if (password.trim().length < 6) {
        this.$message.error('密码最少6位')
        return
      }
      try {
        const res = await this.$api.createAdmin({
          email: email.trim(),
          real_name: real_name.trim(),
          role: 'admin',
          password: password.trim(),
          permissions: [],
          data_scope: 'all',
          task_ids: this.createForm.task_ids || [],
          status: 1
        })
        if (res.data) {
          // 重新加载列表以获取后端生成的 admin_id
          await this.loadAdmins()
          this.$message.success('管理员已创建')
          this.createDialogVisible = false
        }
      } catch (e) {
        this.$message.error('创建失败：' + ((e.response && e.response.data && e.response.data.message) || e.message || '未知错误'))
      }
    },
    async loadAdmins() {
      try {
        const res = await this.$api.fetchAdmins()
        if (res.data) {
          this.$store.commit('SET_ADMINS', res.data)
        }
      } catch (e) {
        console.warn('[AdminManage] 加载失败:', e.message)
      }
    },
    openEditDialog(row) {
      this.editForm = {
        admin_id: row.admin_id,
        email: row.email,
        real_name: row.real_name,
        resetPassword: false,
        newPassword: '',
        task_ids: row.task_ids || []
      }
      this.editDialogVisible = true
    },
    async saveEdit() {
      if (!this.editForm) return
      if (!this.editForm.real_name.trim()) {
        this.$message.error('姓名不能为空')
        return
      }
      try {
        const payload = {
          real_name: this.editForm.real_name.trim(),
          task_ids: this.editForm.task_ids || []
        }
        if (this.editForm.resetPassword) {
          if (!this.editForm.newPassword || this.editForm.newPassword.length < 6) {
            this.$message.error('新密码最少6位')
            return
          }
          if (this.editForm.role === 'super_admin' && this.isSelf(this.editForm)) {
            await this.$confirm('确定重置自己的密码？', '提示', { type: 'warning' })
          }
          payload.password = this.editForm.newPassword
        }
        await this.$api.updateAdmin(this.editForm.admin_id, payload)
        await this.loadAdmins()
        this.$message.success('已保存')
        this.editDialogVisible = false
      } catch (e) {
        console.error('保存失败:', e)
        this.$message.error('保存失败：' + ((e.response && e.response.data && e.response.data.message) || e.message || '未知错误'))
      }
    },
    isSelf(admin) {
      const currentEmail = rawGet('email')
      return currentEmail && admin.email === currentEmail
    },
    openTaskDialog(row) {
      this.currentAdmin = row
      this.taskForm = {
        task_ids: [...(row.task_ids || [])]
      }
      this.taskDialogVisible = true
    },
    async saveTasks() {
      if (!this.currentAdmin) return
      if (!this.taskForm.task_ids || this.taskForm.task_ids.length === 0) {
        this.$message.warning('请至少分配一个任务')
        return
      }
      try {
        await this.$api.updateAdmin(this.currentAdmin.admin_id, {
          task_ids: this.taskForm.task_ids
        })
        await this.loadAdmins()
        this.$message.success('任务分配已保存')
        this.taskDialogVisible = false
      } catch (e) {
        this.$message.error('保存失败：' + ((e.response && e.response.data && e.response.data.message) || e.message || '未知错误'))
      }
    },
    async toggleStatus(row) {
      const action = row.status === 1 ? '禁用' : '启用'
      try {
        await this.$confirm(`确定${action}该管理员账号？`, '提示')
        await this.$api.updateAdmin(row.admin_id, {
          status: row.status === 1 ? 0 : 1
        })
        await this.loadAdmins()
        this.$message.success(`已${action}`)
      } catch (e) {
        // 取消或失败
      }
    },
    canDeleteSuperAdmin(row) {
      const superCount = this.admins.filter(a => a.role === 'super_admin').length
      return superCount > 1
    },
    canDelete(row) {
      if (row.role === 'super_admin') {
        return this.canDeleteSuperAdmin(row)
      }
      return true
    },
    async removeAdmin(row) {
      if (row.role === 'super_admin') {
        const superCount = this.admins.filter(a => a.role === 'super_admin').length
        if (superCount <= 1) {
          this.$message.error('系统至少需要保留一个超级管理员')
          return
        }
      }
      try {
        await this.$confirm(`确定删除管理员 ${row.real_name}？此操作不可撤销。`, '提示', { type: 'warning' })
        await this.$api.deleteAdmin(row.admin_id)
        await this.loadAdmins()
        this.$message.success('已删除')
      } catch (e) {
        // 取消或失败
      }
    }
  }
}
</script>

<style scoped>
.stat-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}
.stat-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 20px 16px;
  text-align: center;
  box-shadow: var(--shadow-sm);
}
.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: var(--color-text);
  line-height: 1.2;
}
.stat-label {
  font-size: 13px;
  color: var(--color-text-muted);
  margin-top: 6px;
}
.toolbar { display: flex; align-items: center; margin-bottom: var(--space-lg); }
.table-wrapper { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); overflow: hidden; box-shadow: var(--shadow-sm); }
.data-table { width: 100%; }
.data-table >>> th { background: var(--color-bg) !important; color: var(--color-text) !important; font-weight: 600 !important; }
.action-edit { color: var(--color-primary) !important; }
.action-disable { color: var(--color-high) !important; }
.text-muted { color: var(--color-text-muted); font-size: 13px; }

.section-title { font-size: 14px; font-weight: 600; color: var(--color-text); margin-bottom: 16px; padding-bottom: 8px; border-bottom: 1px solid var(--color-border); }
.form-hint { font-size: 12px; color: var(--color-text-muted); margin-top: 4px; }
</style>
